import React from 'react';
import { StanButton } from '@/components';
import IcCopy from '@/assets/icons/ic-copy.svg';
import IcCheckCircle from '@/assets/icons/ic-check-circle.svg';
import { getMFADescription, getMFATitle, MFAConfigSource } from '../../../constants/mfa';

interface MFARecoveryCodesProps {
    source: MFAConfigSource;
    recoveryCode: string;
    recoverySaveAction: 'copied' | 'downloaded' | null;
    onCopy: () => void;
    onDownload: () => void;
    onContinue: () => void;
}

export const MFARecoveryCodes: React.FC<MFARecoveryCodesProps> = ({
    source,
    recoveryCode,
    recoverySaveAction,
    onCopy,
    onDownload,
    onContinue,
}) => {
    const title = getMFATitle('save_recovery_codes', source);
    const description = getMFADescription('save_recovery_codes', source);

    const canProceed = recoverySaveAction !== null;

    return (
        <div className='flex flex-col items-center text-center'>
            {/* Header */}
            <div className='mb-4 flex flex-col items-center'>
                <h3 className='mb-2'>{title}</h3>
                <p className='text-gray text-body-sm'>
                    <strong>You can only see this once ⚠️</strong> {description}
                </p>
            </div>

            {/* Recovery Code Card */}
            <div className='bg-neutral-subtle relative mb-3 flex min-h-48 w-full flex-col items-center justify-center rounded-2xl p-4'>
                <button
                    type='button'
                    onClick={onCopy}
                    className='absolute top-2 right-2 cursor-pointer opacity-70 transition-opacity hover:opacity-100'
                    aria-label='Copy recovery code'
                >
                    <IcCopy className='size-6' />
                </button>
                <p className='text-dark w-3/4 text-2xl leading-8'>{recoveryCode}</p>
            </div>

            {/* Action Buttons or Success State */}
            {!canProceed ? (
                <>
                    <p className='text-gray text-body-sm mb-3'>Download or copy your recovery code to continue</p>
                    <div className='flex w-full gap-3'>
                        <StanButton
                            name='mfa-copy-recovery-codes'
                            variant='outline'
                            size='md'
                            className='flex-1'
                            onClick={onCopy}
                        >
                            Copy
                        </StanButton>
                        <StanButton
                            name='mfa-download-recovery-codes'
                            size='md'
                            className='flex-1'
                            onClick={onDownload}
                        >
                            Download
                        </StanButton>
                    </div>
                </>
            ) : (
                <div className='bg-success-subtle mb-3 flex w-full items-center justify-center gap-2 rounded-2xl py-3'>
                    <span className='text-success text-body-md'>
                        Recovery Code {recoverySaveAction === 'copied' ? 'Copied' : 'Downloaded'}!
                    </span>
                    <IcCheckCircle className='text-success size-6' />
                </div>
            )}

            {/* Continue Button */}
            <StanButton
                name='mfa-saved-recovery-codes'
                className='mt-3 w-full'
                size='md'
                disabled={!canProceed}
                onClick={onContinue}
            >
                Continue
            </StanButton>
        </div>
    );
};
